import { useEffect, useState, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Send, CheckCircle, RefreshCw } from 'lucide-react'
import toast from 'react-hot-toast'
import consultasService from '@/services/consultasService'
import PageContainer from '@/components/common/PageContainer'
import PageHeader from '@/components/common/PageHeader'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'

const ESTADO = {
  open: { label: 'Nueva', variant: 'accent' },
  in_progress: { label: 'En proceso', variant: 'warning' },
  resolved: { label: 'Resuelta', variant: 'success' },
  closed: { label: 'Cerrada', variant: 'secondary' },
}

const fechaHora = (d) =>
  d ? new Date(d).toLocaleString('es-SV', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''

// Mensaje del hilo: alinea a la derecha (staff = consultor) o izquierda (user = cliente).
function Mensaje({ c }) {
  const esStaff = c.author?.type === 'staff'
  const esAi = c.author?.type === 'ai'
  return (
    <div className={`flex ${esStaff ? 'justify-end' : 'justify-start'}`}>
      <div className={`min-w-0 max-w-[88%] rounded-2xl px-4 py-2.5 sm:max-w-[78%] ${
        esStaff ? 'bg-primary text-white' : esAi ? 'bg-accent-50 text-foreground dark:bg-accent/15' : 'bg-surface-muted text-foreground'
      }`}>
        <p className="mb-0.5 text-caption opacity-70">{c.author?.name || (esStaff ? 'Tú' : 'Cliente')}</p>
        <p className="whitespace-pre-wrap break-words text-body-sm">{c.content}</p>
        <p className={`mt-1 text-caption ${esStaff ? 'text-white/70' : 'text-subtle'}`}>{fechaHora(c.createdAt)}</p>
      </div>
    </div>
  )
}

export default function ConsultorConsultaDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [consulta, setConsulta] = useState(null)
  const [loading, setLoading] = useState(true)
  const [respuesta, setRespuesta] = useState('')
  const [enviando, setEnviando] = useState(false)

  const cargar = useCallback(async () => {
    try {
      const res = await consultasService.getConsulta(id)
      setConsulta(res?.data || null)
    } catch (e) {
      toast.error(e?.response?.data?.error || 'No se pudo cargar la consulta')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => { cargar() }, [cargar])

  const responder = async () => {
    if (!respuesta.trim()) return
    setEnviando(true)
    try {
      await consultasService.responder(id, respuesta.trim())
      setRespuesta('')
      await cargar()
      toast.success('Respuesta enviada')
    } catch (e) {
      toast.error(e?.response?.data?.error || 'No se pudo enviar la respuesta')
    } finally {
      setEnviando(false)
    }
  }

  const cambiarEstado = async (estado) => {
    try {
      await consultasService.cambiarEstado(id, estado)
      await cargar()
      toast.success(estado === 'resolved' ? 'Consulta marcada como resuelta' : 'Estado actualizado')
    } catch (e) {
      toast.error(e?.response?.data?.error || 'No se pudo cambiar el estado')
    }
  }

  if (loading) {
    return <PageContainer><p className="py-8 text-center text-body-sm text-subtle">Cargando…</p></PageContainer>
  }
  if (!consulta) {
    return (
      <PageContainer>
        <Button variant="ghost" onClick={() => navigate('/consultor/consultas')}><ArrowLeft size={16} /> Volver</Button>
        <p className="py-8 text-center text-body-sm text-subtle">Consulta no encontrada.</p>
      </PageContainer>
    )
  }

  const e = ESTADO[consulta.status] || { label: consulta.status, variant: 'secondary' }
  const resuelto = ['resolved', 'closed'].includes(consulta.status)

  return (
    <PageContainer>
      <PageHeader
        title={consulta.title}
        description={`Cliente: ${consulta.clientCompany || '—'}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={e.variant}>{e.label}</Badge>
            <Button variant="ghost" size="icon" onClick={cargar} aria-label="Refrescar"><RefreshCw size={18} /></Button>
            {!resuelto && (
              <Button variant="outline" onClick={() => cambiarEstado('resolved')}>
                <CheckCircle size={16} /> Marcar resuelta
              </Button>
            )}
          </div>
        }
      />

      <Button variant="ghost" size="sm" className="mb-3 self-start" onClick={() => navigate('/consultor/consultas')}>
        <ArrowLeft size={16} /> Volver a consultas
      </Button>

      <Card>
        <CardContent className="p-4 space-y-4">
          {/* Descripción original */}
          <div className="rounded-lg border border-line bg-surface-muted p-3">
            <p className="mb-1 text-caption uppercase text-subtle">Consulta</p>
            <p className="whitespace-pre-wrap break-words text-body-sm text-foreground">{consulta.description}</p>
          </div>

          {/* Hilo */}
          <div className="space-y-3">
            {(consulta.comments || []).length === 0 ? (
              <p className="py-4 text-center text-body-sm text-subtle">Aún no hay respuestas. Escríbele al cliente abajo.</p>
            ) : (
              consulta.comments.map((c) => <Mensaje key={c.id} c={c} />)
            )}
          </div>

          {/* Caja de respuesta */}
          {consulta.status !== 'closed' && (
            <div className="border-t border-line pt-4">
              <Textarea
                rows={3}
                placeholder="Escribe tu respuesta al cliente…"
                value={respuesta}
                onChange={(ev) => setRespuesta(ev.target.value)}
              />
              <div className="mt-2 flex justify-end">
                <Button className="w-full sm:w-auto" onClick={responder} disabled={enviando || !respuesta.trim()}>
                  <Send size={16} /> {enviando ? 'Enviando…' : 'Responder'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </PageContainer>
  )
}
